import React, { useState } from 'react';
import { Link, type Href } from 'expo-router';
import { Pressable, Text, View } from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';

import { useHydratedWindowDimensions } from '../../hooks/useHydratedWindowDimensions';
import { interRegular, interSemiBold } from '../../lib/fonts';
import { briefExamples } from './researchBriefsContent';
import { briefStyles as s } from './researchBriefsStyles';

export default function BriefConversation({ fontsLoaded }: { fontsLoaded: boolean }) {
    const { i18n } = useLingui();
    const isNarrow = useHydratedWindowDimensions().width < 720;
    const [activeKey, setActiveKey] = useState(briefExamples[0].key);
    const example = briefExamples.find((item) => item.key === activeKey)!;
    const regular = fontsLoaded && interRegular;
    const semiBold = fontsLoaded && interSemiBold;

    return (
        <View>
            <Text style={[s.switcherLabel, semiBold]}><Trans>Choose a field to see its morning brief</Trans></Text>
            <View style={s.segmented} accessibilityRole="tablist">
                {briefExamples.map((item) => {
                    const active = item.key === activeKey;
                    return (
                        <Pressable
                            key={item.key}
                            accessibilityRole="tab"
                            aria-selected={active}
                            onPress={() => setActiveKey(item.key)}
                            style={[s.segment, isNarrow && s.segmentNarrow, active && s.segmentActive]}
                        >
                            <FontAwesome6 name={item.icon} size={14} color={active ? '#ea580c' : '#83907f'} />
                            <Text style={[s.segmentText, isNarrow && s.segmentTextNarrow, active && s.segmentTextActive, semiBold]}>
                                {i18n._(item.tab)}
                            </Text>
                        </Pressable>
                    );
                })}
            </View>

            <View style={s.chat}>
                <View style={s.chatHeader}>
                    <View style={s.chatDot} />
                    <Text style={[s.chatHeaderText, semiBold]} numberOfLines={1}>
                        <Trans>Morning brief</Trans> · {i18n._(example.feed)}
                    </Text>
                    <Text style={[s.chatTime, regular]}><Trans>08:00</Trans></Text>
                </View>

                <View style={s.chatBody}>
                    <View>
                        <Text style={[s.speaker, semiBold]}><Trans comment="Speaker label for the AI assistant in the example chat">Assistant</Trans></Text>
                        <Text style={[s.chatText, regular]}><Trans>Good morning. Two new papers in your feed are worth your time.</Trans></Text>
                        {example.picks.map((pick, index) => (
                            <View key={pick.href} style={s.pick}>
                                <Text style={[s.pickNumber, semiBold]}>{index + 1}</Text>
                                <View style={s.pickCopy}>
                                    <Link href={pick.href as Href} target="_blank" style={[s.pickTitle, semiBold]}>
                                        {i18n._(pick.paper)}
                                    </Link>
                                    <Text style={[s.pickNote, regular]}>{i18n._(pick.note)}</Text>
                                </View>
                            </View>
                        ))}
                    </View>

                    <View style={s.question}>
                        <Text style={[s.speaker, semiBold]}><Trans>You</Trans></Text>
                        <Text style={[s.chatText, regular]}>{i18n._(example.question)}</Text>
                    </View>

                    <View>
                        <Text style={[s.speaker, semiBold]}><Trans comment="Speaker label for the AI assistant in the example chat">Assistant</Trans></Text>
                        <Text style={[s.chatText, regular]}>{i18n._(example.answer)}</Text>
                        <Link href={example.source.href as Href} target="_blank" style={[s.sourceLink, semiBold]}>
                            {i18n._(example.source.label)} ↗
                        </Link>
                    </View>
                </View>

                <View style={s.composer} aria-hidden>
                    <Text style={[s.composerText, regular]}><Trans>Ask about these papers…</Trans></Text>
                    <View style={s.composerButton}>
                        <FontAwesome6 name="arrow-up" size={12} color="#5d6c5b" />
                    </View>
                </View>
            </View>

            <Text style={[s.caption, regular]}>
                <Trans>Example conversation. The papers are real, so you can follow the links and check the answers.</Trans>
            </Text>
        </View>
    );
}
